import { FC } from 'react';

import { startOfDay } from 'date-fns';
import { formatDate } from 'utils/date';

import auctionCampaignPeriodRadioButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_period_radio_button_click';
import { Cell, CellText, Radio, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { AuctionDateEnd } from 'components/AuctionBuilder/AuctionPeriod/components/AuctionDateEnd';
import { AuctionDateStart } from 'components/AuctionBuilder/AuctionPeriod/components/AuctionDateStart';
import { checkIsCampaignStartDateLessThanToday } from 'components/AuctionBuilder/utils';
import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';

import {
    AuctionPeriodType,
    auctionSelector,
    auctionSelectorNonNullable,
    BiddingStrategy,
    resetDates,
    setEndDate,
    setIsDatesChanged,
    setSelectedPeriodType,
    setStartDate,
} from 'store/models/auctionData';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/AuctionBuilder/AuctionPeriod/AuctionPeriod.module.less';

const TrlKeys = {
    label: 'AuctionBuilder.CampaignPeriod.label',
    radioUnlimited: 'AuctionBuilder.CampaignPeriod.radio.unlimited',
    radioManual: 'AuctionBuilder.CampaignPeriod.radio.manual',
};

export const AuctionPeriod: FC = () => {
    const vacancyId = useSelector(auctionSelector.vacancyId);
    const campaignId = useSelector(auctionSelector.auctionCampaign)?.campaignId;
    const campaignStartDate = useSelector(auctionSelector.auctionCampaign)?.startAt;
    const biddingStrategy = useSelector(auctionSelectorNonNullable.biddingStrategy);
    const selectedPeriodType = useSelector(auctionSelectorNonNullable.selectedPeriodType);

    const trl = useTrls();
    const dispatch = useDispatch();

    const isCampaignStartDateLessThanToday = checkIsCampaignStartDateLessThanToday(campaignStartDate);

    const handlePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPeriodType = event.target.value as AuctionPeriodType;

        auctionCampaignPeriodRadioButtonClick({
            vacancyId,
            campaignId,
            periodType: newPeriodType,
        });

        if (newPeriodType === AuctionPeriodType.Unlimited) {
            if (isCampaignStartDateLessThanToday) {
                dispatch(setEndDate(null));
            } else {
                dispatch(resetDates(null));
            }
            dispatch(setIsDatesChanged(false));
        }

        if (newPeriodType === AuctionPeriodType.Manual && !isCampaignStartDateLessThanToday) {
            dispatch(setStartDate(formatDate(startOfDay(new Date()))));
        }

        dispatch(setSelectedPeriodType(newPeriodType));
    };

    return (
        <MagritteFormField
            label={trl(TrlKeys.label)}
            input={
                <>
                    {biddingStrategy === BiddingStrategy.Manual && (
                        <VSpacingContainer default={12}>
                            <Cell
                                left={
                                    <Radio
                                        name="period"
                                        value={AuctionPeriodType.Unlimited}
                                        checked={selectedPeriodType === AuctionPeriodType.Unlimited}
                                        dataQaContainer="period-radio-unlimited"
                                        data-qa="period-radio-unlimited-input"
                                        onChange={handlePeriodChange}
                                    />
                                }
                            >
                                <CellText>{trl(TrlKeys.radioUnlimited)}</CellText>
                            </Cell>
                            <Cell
                                left={
                                    <Radio
                                        name="period"
                                        value={AuctionPeriodType.Manual}
                                        checked={selectedPeriodType === AuctionPeriodType.Manual}
                                        dataQaContainer="period-radio-manual"
                                        data-qa="period-radio-manual-input"
                                        onChange={handlePeriodChange}
                                    />
                                }
                            >
                                <CellText>{trl(TrlKeys.radioManual)}</CellText>
                            </Cell>
                        </VSpacingContainer>
                    )}
                    {(biddingStrategy === BiddingStrategy.Auto || selectedPeriodType === AuctionPeriodType.Manual) && (
                        <>
                            {selectedPeriodType === AuctionPeriodType.Manual &&
                                biddingStrategy === BiddingStrategy.Manual && <VSpacing default={12} />}
                            <div className={styles.periodWrapper}>
                                <AuctionDateStart />
                                <AuctionDateEnd />
                            </div>
                        </>
                    )}
                </>
            }
        />
    );
};
